import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { animeRoles } from "@/mocks/roles";
import Tag from "@/components/base/Tag";
import RoleCard from "@/components/base/RoleCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import FavoriteButton from "@/components/base/FavoriteButton";
import ShareModal from "@/components/base/ShareModal";

export default function RoleDetail() {
  const { id = "" } = useParams();
  const role = animeRoles.find((r) => r.id === id);
  const [activeImage, setActiveImage] = useState(0);
  const [shareOpen, setShareOpen] = useState(false);

  useSeo({
    title: role ? `${role.name} - 萝莉动漫主题站` : "角色不存在 - 萝莉动漫主题站",
    description: role
      ? `${role.name}（${role.alias}）来自《${role.work}》，${role.description}`
      : "该角色不存在或已下架。",
    keywords: role ? `${role.name},${role.work},${role.tags.join(",")},萝莉角色` : "萝莉角色"
  });

  const related = useMemo(() => {
    if (!role) return [];
    return animeRoles
      .filter((r) => r.id !== role.id)
      .map((r) => {
        const sameWork = r.work === role.work ? 3 : 0;
        const sharedTags = r.tags.filter((t) => role.tags.includes(t)).length * 2;
        const sharedAttrs = r.attributes.filter((a) => role.attributes.includes(a)).length;
        return { role: r, score: sameWork + sharedTags + sharedAttrs };
      })
      .sort((a, b) => b.score - a.score || b.role.popularity - a.role.popularity)
      .slice(0, 4)
      .map((item) => item.role);
  }, [role]);

  if (!role) {
    return (
      <div className="mx-auto flex max-w-[900px] flex-col items-center justify-center px-6 py-24 text-center">
        <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-primary-100 text-primary-500">
          <i className="ri-emotion-sad-line text-4xl"></i>
        </div>
        <h1 className="text-2xl text-foreground-950">角色不存在或已下架</h1>
        <p className="mt-3 text-sm text-foreground-600">
          你访问的角色可能已经调整或移除，不如去角色库看看其他角色吧。
        </p>
        <div className="mt-6 flex gap-3">
          <Link
            to="/roles"
            className="rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
          >
            浏览角色库
          </Link>
          <Link
            to="/"
            className="rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const images = [role.cover, ...role.gallery];
  const infoList = [
    { label: "所属作品", value: role.work, icon: "ri-book-2-line" },
    { label: "性格", value: role.personality, icon: "ri-emotion-line" },
    { label: "年龄", value: role.age, icon: "ri-calendar-line" },
    { label: "声优", value: role.cv, icon: "ri-mic-line" },
    { label: "首次登场", value: role.firstAppear, icon: "ri-movie-line" }
  ];

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "角色列表", to: "/roles" },
          { label: role.name }
        ]}
      />

      <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-[420px_1fr]">
        <div>
          <div className="overflow-hidden rounded-lg border border-background-200 bg-background-100">
            <div className="relative aspect-[3/4] w-full">
              <img
                src={images[activeImage]}
                alt={`${role.name} 萝莉动漫角色立绘`}
                title={`${role.name} - ${role.work}`}
                className="h-full w-full object-top"
              />
              {role.isNew && (
                <span className="absolute left-4 top-4 rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-background-50">
                  新角色
                </span>
              )}
            </div>
          </div>
          {images.length > 1 && (
            <div className="mt-3 flex gap-3">
              {images.map((img, i) => (
                <button
                  key={img}
                  onClick={() => setActiveImage(i)}
                  className={`h-20 w-20 overflow-hidden rounded-md border-2 transition-colors cursor-pointer ${
                    activeImage === i ? "border-primary-500" : "border-transparent"
                  }`}
                >
                  <img src={img} alt={`${role.name} 立绘 ${i + 1}`} className="h-full w-full object-top" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="flex flex-wrap items-center gap-3">
            {role.tags.map((t) => (
              <Tag key={t} tone="secondary">
                {t}
              </Tag>
            ))}
          </div>
          <h1 className="mt-3 text-3xl text-foreground-950">{role.name}</h1>
          <p className="mt-1 text-sm text-foreground-500">别名 · {role.alias}</p>

          <div className="mt-5 flex flex-wrap gap-3">
            <FavoriteButton type="role" id={role.id} withLabel />
            <button
              onClick={() => setShareOpen(true)}
              className="inline-flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer"
            >
              <i className="ri-share-forward-line"></i>
              分享
            </button>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {infoList.map((info) => (
              <div
                key={info.label}
                className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                  <i className={`${info.icon} text-lg`}></i>
                </span>
                <div className="min-w-0">
                  <p className="text-xs text-foreground-500">{info.label}</p>
                  <p className="mt-0.5 text-sm text-foreground-900">{info.value}</p>
                </div>
              </div>
            ))}
            <div className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className="ri-fire-line text-lg"></i>
              </span>
              <div>
                <p className="text-xs text-foreground-500">人气指数</p>
                <p className="mt-0.5 text-sm text-foreground-900">
                  {role.popularity.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8">
            <h2 className="mb-3 flex items-center gap-2 text-xl text-foreground-950">
              <i className="ri-quill-pen-line text-primary-500"></i>
              角色简介
            </h2>
            <p className="text-sm leading-loose text-foreground-700">{role.description}</p>
          </div>

          <div className="mt-8">
            <h2 className="mb-3 flex items-center gap-2 text-xl text-foreground-950">
              <i className="ri-book-read-line text-secondary-500"></i>
              故事背景
            </h2>
            <p className="text-sm leading-loose text-foreground-700">{role.story}</p>
          </div>

          <div className="mt-8 rounded-lg border border-secondary-200 bg-secondary-50 p-5">
            <h2 className="mb-2 flex items-center gap-2 text-lg text-foreground-950">
              <i className="ri-film-line text-secondary-600"></i>
              所属作品 ·《{role.work}》
            </h2>
            <p className="text-sm leading-relaxed text-foreground-700">{role.workDesc}</p>
            <Link
              to={`/news?q=${encodeURIComponent(role.work)}`}
              className="mt-3 inline-flex items-center gap-1 text-sm text-secondary-700 transition-colors hover:text-secondary-800"
            >
              查看相关资讯
              <i className="ri-arrow-right-s-line"></i>
            </Link>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            {role.attributes.map((a) => (
              <Link
                key={a}
                to={`/roles?attr=${encodeURIComponent(a)}`}
                className="rounded-full border border-background-300 bg-background-100 px-3 py-1 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                # {a}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-14">
          <div className="mb-6 flex items-end justify-between">
            <div>
              <h2 className="text-2xl text-foreground-950">相关角色推荐</h2>
              <p className="mt-1 text-sm text-foreground-600">同作品或相似属性的角色，也许你会喜欢</p>
            </div>
            <Link
              to="/roles"
              className="hidden items-center gap-1 text-sm text-primary-600 transition-colors hover:text-primary-700 sm:flex"
            >
              更多角色
              <i className="ri-arrow-right-s-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {related.map((r) => (
              <RoleCard key={r.id} role={r} />
            ))}
          </div>
        </section>
      )}

      <ShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        title={`${role.name} - 萝莉动漫主题站`}
        path={`/role/${role.id}`}
      />
    </div>
  );
}